/**
 * Prefix shapes and group membership (spec 011).
 *
 * THE DEFECT THIS EXISTS FOR. A model page asks the provider with `model=`,
 * which is an EXACT match, and covers the other spellings by fanning out over
 * the reviewed index — capped at six requests. Two things make that fail:
 * the vendor's catalogue truncates names to ten characters for one of its two
 * sources, so index entries like `X5 XDRIVE4` match no live lot at all; and a
 * wide group has far more spellings than the cap. Measured 2026-09-03:
 * `/aukcje/honda/civic` spent SIX requests over 23 spellings and rendered
 * exactly ONE — `CIVIC` — while a single prefix query returns `CIVIC`,
 * `CIVIC SI` and `CIVIC HYBRID`.
 *
 * The provider's undocumented `q` parameter is a prefix match, so one request
 * reaches every trim of a model at once. What it CANNOT do is reconcile `F150`
 * with `F-150`: they differ at the second character, so no prefix reaches both.
 * Only reviewed data knows they are one truck. Hence this module's two halves —
 * which prefixes to ASK for, and which returned models to KEEP.
 *
 * DELIBERATELY PURE, and it stays that way. No provider types, no I/O, no import
 * of the index. Both the app and `scripts/build-model-index.mjs` use it, and the
 * yields the sweep records must describe the same rules the app applies — a
 * second copy in the script is precisely the drift spec 009 was designed to
 * prevent. `modelSpellings.ts` is shared the same way for the same reason.
 */

/**
 * A group's spellings that share a leading token are reachable by one query, so
 * the leading token is the unit. Splitting on whitespace is the whole rule:
 * `MX-5 MIATA RF` → `MX-5`, `F-150 LIGHTNING` → `F-150`, `X5 XDRIVE4` → `X5`.
 */
const leadingToken = (spelling: string): string => spelling.trim().split(/\s+/)[0] ?? '';

/**
 * The shortest space-free strings that reach a group's spellings.
 *
 * A SPACE IS NEVER ALLOWED IN THE RESULT, and the reason is worth stating
 * because the obvious version of it is wrong. The spec assumed `q` returns HTTP
 * 502 for any value containing a space. It does not: measured, `q=X5 M` and
 * `q=X5 MM` return 502 repeatably — three attempts each — while `q=X5 X` and
 * `q=MX-5 MIATA` both return 200. The failure is value-dependent, undocumented
 * and returns an HTML gateway page rather than the API's JSON, which points at
 * an upstream timeout rather than validation.
 *
 * So the rule is not "spaces are rejected" but "we cannot predict which spaced
 * values are rejected, therefore we send none". A prohibition we can enforce
 * beats a rule we cannot predict.
 *
 * AN EMPTY RESULT IS A VALID ANSWER, not a failure. It means "query this group
 * by exact spellings instead", and 36 groups return it — including
 * Mercedes-Benz E-Class (26 spellings), S-Class (22) and C-Class (20), whose
 * every spelling is a single letter, a space, then a number. Returning a
 * one-character prefix for those is a SEPARATE decision the caller makes on
 * measured evidence, because it is right for Mercedes and useless for BMW:
 * `q=E` reaches 19 of 20 rows on-group, `q=3` reaches 0 of 20.
 */
export const prefixesFor = (spellings: string[]): string[] =>
  minimise(spellings).filter(token => token.length >= MIN_PREFIX_LENGTH);

/**
 * Below this a prefix is not queried without evidence.
 *
 * Two characters is also the floor for admitting an UNSEEN spelling below, and
 * the two uses are the same judgement: one character is not enough information
 * to tell a model from its neighbours. `F` would eat the F-150 page — the trap
 * spec 007 had to fix once already.
 */
export const MIN_PREFIX_LENGTH = 2;

/**
 * Every leading token of a group, including single-character ones.
 *
 * `prefixesFor` withholds these because querying one is only sometimes worth a
 * request. The sweep needs to SEE them to measure whether it is, which is the
 * one thing runtime cannot work out for itself.
 */
export const candidatePrefixesFor = (spellings: string[]): string[] => minimise(spellings);

/**
 * Deduped leading tokens, with any token another one already reaches removed.
 *
 * ONLY A USABLE PREFIX MAY SUBSUME ANOTHER, and getting that wrong emptied two
 * of the most important groups on the site. `F 150` contributes the token `F`;
 * because `F` is a prefix of both `F150` and `F-150`, a naive minimisation drops
 * them both — and then the two-character floor removes `F` as well, leaving the
 * Ford F-150 page with NO query at all. BMW's `3 SERIES` failed identically via
 * `3`. So a token too short to be queried is not allowed to stand in for one
 * that can be.
 *
 * `F150` and `F-150` survive as a pair either way: neither is a prefix of the
 * other, which is the whole reason that group needs two requests.
 */
const minimise = (spellings: string[]): string[] => {
  const tokens = [...new Set(spellings.map(leadingToken).filter(Boolean))];

  return tokens
    .filter(
      token =>
        !tokens.some(
          other =>
            other !== token &&
            other.length >= MIN_PREFIX_LENGTH &&
            other.length < token.length &&
            token.startsWith(other)
        )
    )
    // Shortest first, so a request budget that binds spends on the broadest
    // query rather than a trim-specific one.
    .sort((a, b) => a.length - b.length || a.localeCompare(b));
};

/** A separator, or the end of the string — see `belongsToGroup`. */
const SEPARATORS = new Set([' ', '-', '/']);

type TMembershipArgs = {
  /** The reviewed canonical of the group the visitor asked for. */
  requestedCanonical: string;
  /** The model exactly as the provider spelled it on the returned lot. */
  lotModel: string;
  /**
   * The index's opinion about a spelling, INJECTED rather than imported.
   *
   * The app passes the committed index; the sweep passes its in-progress
   * groups. Neither can drift from the other's rules, and this module stays
   * free of the index it would otherwise have to import.
   */
  resolveGroup: (spelling: string) => string | null;
};

/**
 * Does a lot returned by a prefix query actually belong to the requested model?
 *
 * THIS IS THE ONLY THING BETWEEN A PREFIX QUERY AND A FUSION ON THE F-150 PAGE.
 * A prefix over-matches by nature: measured, `q=F` on Ford returns `FUSION`,
 * `FOCUS` and `F-750`; `q=MX` on Mazda returns `MX-3`; `q=3` on BMW returns
 * twenty rows of which NOT ONE belongs to the `3 SERIES` group. Coverage bought
 * with wrong cars is worse than no coverage, so precision is enforced here
 * rather than hoped for upstream.
 *
 * TWO STEPS, AND THE ORDER IS THE REQUIREMENT (FR-005 over FR-006):
 *
 *  1. The index decides wherever it has an opinion. It is the only mechanism
 *     that can put `F150` and `F-150` in one group, and it carries decisions a
 *     person reviewed — a `detach` must survive a string rule that disagrees.
 *
 *  2. Only where the index has never seen the spelling does the separator rule
 *     apply, and narrowly: the canonical must be at least two characters, the
 *     lot's model must start with it, and what follows must be a separator or
 *     nothing. This is the window between a vendor inventing a trim name and
 *     the next sweep reviewing it — not a general-purpose matcher.
 *
 * IT REFUSES BY DEFAULT. An input it cannot reason about is not admitted, so
 * its failure mode is a THIN page — visible, logged, and fixable — rather than
 * a WRONG page, which nobody notices until a customer does.
 */
export const belongsToGroup = ({
  requestedCanonical,
  lotModel,
  resolveGroup,
}: TMembershipArgs): boolean => {
  const wanted = requestedCanonical.trim().toUpperCase();
  const actual = lotModel.trim().toUpperCase();

  if (!wanted || !actual) return false;

  // Step 1 — the index, wherever it has an opinion.
  const known = resolveGroup(lotModel);

  if (known !== null) return known.trim().toUpperCase() === wanted;

  // Step 2 — the narrow admission for a spelling nobody has reviewed yet.
  if (wanted.length < MIN_PREFIX_LENGTH) return false;

  if (!actual.startsWith(wanted)) return false;

  const next = actual.charAt(wanted.length);

  return next === '' || SEPARATORS.has(next);
};

/**
 * What the sweep last observed about one catalogue spelling (spec 011, FR-013).
 *
 *  - `live`                 the sample contained this exact model string
 *  - `likely-truncated`     at the character limit, and a LONGER sampled model starts with it
 *  - `suspected-truncated`  at the character limit, nothing longer seen
 *  - `extinct`              not at the limit, and a sibling spelling WAS seen
 *
 * NOTHING HERE IS PROOF, and an earlier version of this claimed it was. The
 * status was called `confirmed-truncated` on the strength of "a longer sampled
 * model starts with it" — and the gate then found `CAN-AM MAVERICK` labelled
 * truncated while `model=MAVERICK` returns four real lots. `MAVERICK` and
 * `MAVERICK X3` are simply two models, and the eight-character name was never
 * truncated at all. Only an exact provider lookup could settle it, and the sweep
 * cannot afford one per spelling, so the label says "likely" and means it.
 */
export type TSpellingStatus =
  | 'live'
  | 'likely-truncated'
  | 'suspected-truncated'
  | 'extinct'
  /**
   * The sample never reached this corner of the inventory, so nothing is known.
   *
   * DISTINCT FROM `extinct` ON PURPOSE. A sweep samples a couple of pages per
   * make, which for a large make is a few dozen lots out of thousands — so "the
   * sample did not contain it" is overwhelmingly weaker evidence than "its cars
   * are gone". The first honest run of this audit labelled 1,758 spellings
   * extinct, and almost all of them were merely unsampled; a review queue of
   * that size is noise, and noise is what the audit exists to remove.
   *
   * Promoted to `extinct` only when a SIBLING spelling in the same group was
   * seen live — proof the sample did reach that model, so this spelling's
   * absence is a fact about inventory rather than about the sample.
   */
  | 'unseen';

/**
 * The catalogue's character limit, for one of the two sources.
 *
 * TEN CHARACTERS IS A SUSPICION, NOT A PROOF, and the spec overstated this.
 * Measured over the committed index: 1,591 of 5,096 spellings are exactly ten
 * characters against 449 at nine and 61 at eleven — a cliff, not a
 * distribution. But the longest spelling is FORTY characters, and 131 exceed
 * ten (`F-150 SUPERCREW`, `MX-5 MIATA AUTOMATIC`, `4 SERIES GRAN COUPE`), so
 * the limit plainly applies to one source and not the other.
 *
 * A length-only rule would therefore libel a genuine ten-character model name.
 * Only a longer live string sharing the prefix promotes a suspicion to a fact.
 */
const CATALOGUE_TRUNCATION_LENGTH = 10;

/**
 * Classify one spelling against what the inventory sample actually saw.
 *
 * Nothing is ever DROPPED on the strength of this (FR-014). Dropping discards
 * data the catalogue really returned only for the next sweep to re-add it; the
 * point of the label is to make waste visible, because today six of BMW X5's
 * fifteen entries consume five of six requests per page view and nothing
 * reports it.
 */
export const classifySpelling = (
  spelling: string,
  sampledModels: ReadonlySet<string>
): TSpellingStatus => {
  const wanted = spelling.trim().toUpperCase();

  if (!wanted) return 'extinct';

  if (sampledModels.has(wanted)) return 'live';

  let longerSeen = false;

  for (const sampled of sampledModels) {
    // A longer live name sharing this prefix raises the suspicion:
    // `X5 XDRIVE4` matches nothing, but the market holds `X5 XDRIVE40I`.
    if (sampled.length > wanted.length && sampled.startsWith(wanted)) longerSeen = true;
  }

  /*
   * The length test comes FIRST. A name shorter than the limit was not cut by a
   * limit it never reached, however many longer siblings share its prefix —
   * that is what made an eight-character `MAVERICK` look truncated.
   */
  if (wanted.length !== CATALOGUE_TRUNCATION_LENGTH) return 'extinct';

  return longerSeen ? 'likely-truncated' : 'suspected-truncated';
};
